// src/pages/TicketsPage.jsx
//
// Direction A — Tickets list:
//   - Page header: eyebrow + "Tickets" + "New ticket" CTA (→ /tickets/new)
//   - Segmented filter bar: All / Open / In Progress / Urgent (`?summary=` is source of truth)
//   - List panel: column headings + rows (ID, title + latest comment, property, type, priority, status, age)
//   - Click row to open the edit/details dialog (preserves existing edit + comments flow).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Autocomplete,
  Typography,
  Divider,
  Popover,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import Layout from "../components/layout/Layout";
import Panel from "../components/brand/Panel";
import Badge from "../components/brand/Badge";
import { fonts } from "../theme/tokens";

import {
  getTickets,
  getTicketStats,
  updateTicket,
  getTicketComments,
  createTicketComment,
} from "../api/ticketApi";
import { getApiErrorMessage } from "../utils/concurrency";
import { getPropertyLPE1Forms, searchProperties } from "../api/propertyApi";
import { getUsersForAssignment } from "../api/userApi";

const PAGE_SIZE = 20;
/** Must match ticket row `gridTemplateColumns` below and in the list header. */
const TICKET_LIST_GRID = "60px 1fr 140px 110px 110px 110px 60px";

const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Urgent"];
const STATUS_OPTIONS = ["Open", "In Progress", "Resolved", "Closed"];
const TYPE_OPTIONS = ["Maintenance", "Major Works", "Management Pack"];

// Filters available on the segmented bar.
// Each maps to the params we send to the backend list endpoint.
const FILTERS = [
  { key: "all", label: "All tickets", showResolved: true, params: {} },
  { key: "open", label: "Open", showResolved: false, params: { status: "Open" } },
  {
    key: "in_progress",
    label: "In Progress",
    showResolved: false,
    params: { status: "In Progress" },
  },
  {
    key: "urgent",
    label: "Urgent",
    showResolved: false,
    params: { status: "Open,In Progress", priority: "Urgent" },
  },
];

const formatRelativeAge = (dateValue) => {
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.floor(mo / 12)}y`;
};

export default function TicketsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Single source of truth: URL `summary` (sidebar links, dashboard, bookmarks).
  // Do not keep a separate filter state — it goes stale when the search string
  // changes without a remount (e.g. /tickets → /tickets?summary=open).
  const summaryParam = searchParams.get("summary") ?? "";
  const filterKey = useMemo(() => {
    if (summaryParam && FILTERS.some((f) => f.key === summaryParam)) return summaryParam;
    if (summaryParam === "high_priority") return "urgent";
    return "all";
  }, [summaryParam]);

  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    in_progress: 0,
    high_priority_open: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  /** Total rows for current filters (from API); list may be one page only. */
  const [listTotal, setListTotal] = useState(0);

  const [users, setUsers] = useState([]);
  const [editingTicket, setEditingTicket] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editError, setEditError] = useState("");
  const [editProperty, setEditProperty] = useState(null);
  const [editPropertyOptions, setEditPropertyOptions] = useState([]);
  const [editPropertyLoading, setEditPropertyLoading] = useState(false);
  const [editPropertyEnquiryForm, setEditPropertyEnquiryForm] = useState(null);
  const [editPropertyEnquiryOptions, setEditPropertyEnquiryOptions] = useState([]);
  const [editPropertyEnquiryLoading, setEditPropertyEnquiryLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  /** Extra list filters (API: priority, type, property_id) — applied from Filters popover. */
  const [advFilters, setAdvFilters] = useState({
    priority: "",
    type: "",
    property: null,
  });
  const [advDraft, setAdvDraft] = useState({
    priority: "",
    type: "",
    property: null,
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterPropOptions, setFilterPropOptions] = useState([]);
  const [filterPropLoading, setFilterPropLoading] = useState(false);

  const loadRequestId = useRef(0);
  const filterOpen = Boolean(filterAnchorEl);
  const hasAdvFilters = Boolean(
    advFilters.priority || advFilters.type || advFilters.property?.id
  );

  const loadTicketStats = useCallback(async () => {
    try {
      setStats(await getTicketStats());
    } catch (e) {
      console.error("Tickets: failed to load stats", e);
    }
  }, []);

  // Initial reference data — assignable users + ticket counts (KPI chips / sidebar source of truth for counts).
  useEffect(() => {
    (async () => {
      try {
        const data = await getUsersForAssignment();
        setUsers(data || []);
      } catch (e) {
        console.error("Tickets: failed to load assignable users", e);
      }
      await loadTicketStats();
    })();
  }, [loadTicketStats]);

  const mapPropertyEnquiryOptions = useCallback((forms) => {
    return (forms || []).map((form) => {
      const formType = form?.form_type || "LPE1";
      const unitLabel = form?.unit_name || `Unit ${form?.unit_id ?? "-"}`;
      return {
        id: form.id,
        form_type: formType,
        unit_id: form.unit_id,
        unit_name: unitLabel,
        label: `${formType} · ${unitLabel} · #${form.id}`,
      };
    });
  }, []);

  const loadTickets = useCallback(async () => {
    const id = ++loadRequestId.current;
    setLoading(true);
    setError("");
    try {
      const filter = FILTERS.find((f) => f.key === filterKey) || FILTERS[0];
      const params = {
        page,
        page_size: PAGE_SIZE,
        sort: "created_at_utc",
        dir: "desc",
        // Explicit strings — some stacks omit boolean false from query strings.
        show_resolved: filter.showResolved ? "true" : "false",
        ...filter.params,
      };
      if (advFilters.priority) params.priority = advFilters.priority;
      if (advFilters.type) params.type = advFilters.type;
      if (advFilters.property?.id) params.property_id = advFilters.property.id;
      const res = await getTickets(params);
      if (loadRequestId.current === id) {
        setTickets(res?.results || []);
        setTotalPages(Number(res?.total_pages || 1));
        setListTotal(Number(res?.total ?? res?.results?.length ?? 0));
      }
    } catch (e) {
      if (loadRequestId.current === id) {
        console.error("Tickets: failed to load", e);
        setError("Unable to load tickets. Please try again later.");
      }
    } finally {
      if (loadRequestId.current === id) setLoading(false);
    }
  }, [filterKey, page, advFilters.priority, advFilters.type, advFilters.property?.id]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Open the edit dialog if `?ticketId=...` is present.
  useEffect(() => {
    const ticketIdParam = searchParams.get("ticketId");
    if (!ticketIdParam || tickets.length === 0 || editingTicket) return;
    const match = tickets.find((t) => String(t.id) === String(ticketIdParam));
    if (match) handleEditOpen(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, searchParams]);

  const applyFilter = (next) => {
    setPage(1);
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("summary");
    else params.set("summary", next);
    setSearchParams(params);
  };

  const openFiltersPopover = (event) => {
    setAdvDraft(advFilters);
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterPropertySearch = async (q) => {
    if (!q || q.length < 1) {
      setFilterPropOptions([]);
      return;
    }
    setFilterPropLoading(true);
    try {
      const results = await searchProperties(q, 20);
      setFilterPropOptions(
        (results || []).map((p) => ({
          id: p.property_id,
          name: p.property_name,
          address_line1: p.address_line1,
          city: p.city,
        }))
      );
    } catch (e) {
      console.error("Tickets: filter property search failed", e);
    } finally {
      setFilterPropLoading(false);
    }
  };

  const applyAdvFilters = () => {
    setAdvFilters(advDraft);
    setPage(1);
    setFilterAnchorEl(null);
  };

  const clearAdvFilters = () => {
    const empty = { priority: "", type: "", property: null };
    setAdvDraft(empty);
    setAdvFilters(empty);
    setFilterPropOptions([]);
    setPage(1);
    setFilterAnchorEl(null);
  };

  const handleEditOpen = async (ticket) => {
    setEditingTicket(ticket);
    setEditError("");
    setEditForm({
      title: ticket.title || "",
      description: ticket.description || "",
      priority: ticket.priority || "Medium",
      status: ticket.status || "Open",
      type: ticket.type || "",
      requester_name: ticket.requester_name || "",
      assigned_to_id: ticket.assigned_to_id || "",
      property_id: ticket.property_id || "",
      property_enquiry_form_type: ticket.property_enquiry_form_type || "",
      property_enquiry_form_id: ticket.property_enquiry_form_id || "",
    });
    setEditProperty(
      ticket.property_id
        ? {
            id: ticket.property_id,
            name: ticket.property_name || `Property ${ticket.property_id}`,
          }
        : null
    );
    setEditPropertyEnquiryForm(null);
    setEditPropertyEnquiryOptions([]);
    setComments([]);
    setCommentText("");
    try {
      const data = await getTicketComments(ticket.id);
      setComments(data || []);
    } catch (e) {
      console.error("Tickets: failed to load comments", e);
    }
  };

  const handleEditClose = () => {
    setEditingTicket(null);
    setEditForm(null);
    setEditError("");
    setEditProperty(null);
    setEditPropertyEnquiryForm(null);
    setEditPropertyEnquiryOptions([]);
    setComments([]);
    setCommentText("");
    if (searchParams.get("ticketId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("ticketId");
      setSearchParams(next);
    }
  };

  const handleEditSubmit = async () => {
    if (!editingTicket || !editForm) return;
    if (editForm.type === "Management Pack" && !editPropertyEnquiryForm) {
      setEditError("Property enquiry form is required for Management Pack tickets.");
      return;
    }
    setSubmitting(true);
    setEditError("");
    try {
      await updateTicket(editingTicket.id, {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        priority: editForm.priority,
        status: editForm.status,
        type: editForm.type,
        requester_name: editForm.requester_name.trim(),
        property_id: editProperty?.id || null,
        assigned_to_id: editForm.assigned_to_id ? Number(editForm.assigned_to_id) : null,
        property_enquiry_form_type:
          editForm.type === "Management Pack" ? editPropertyEnquiryForm?.form_type || null : null,
        property_enquiry_form_id:
          editForm.type === "Management Pack" ? editPropertyEnquiryForm?.id || null : null,
        row_version: editingTicket.row_version,
      });
      await Promise.all([loadTickets(), loadTicketStats()]);
      handleEditClose();
    } catch (e) {
      console.error("Tickets: failed to update", e);
      setEditError(getApiErrorMessage(e, "Unable to update ticket."));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    let ignore = false;

    const loadEditPropertyEnquiries = async () => {
      if (!editingTicket || editForm?.type !== "Management Pack") {
        setEditPropertyEnquiryForm(null);
        setEditPropertyEnquiryOptions([]);
        setEditPropertyEnquiryLoading(false);
        return;
      }

      if (!editProperty?.id) {
        setEditPropertyEnquiryForm(null);
        setEditPropertyEnquiryOptions([]);
        return;
      }

      setEditPropertyEnquiryLoading(true);
      try {
        const forms = [];
        let pageNumber = 1;
        let totalPages = 1;

        while (pageNumber <= totalPages) {
          const data = await getPropertyLPE1Forms(editProperty.id, {
            page: pageNumber,
            page_size: 100,
          });
          forms.push(...(data?.results || []));
          totalPages = data?.total_pages || 1;
          pageNumber += 1;
        }

        if (ignore) return;

        const nextOptions = mapPropertyEnquiryOptions(forms);
        setEditPropertyEnquiryOptions(nextOptions);
        setEditPropertyEnquiryForm(() => {
          const selectedId = editForm?.property_enquiry_form_id;
          const selectedType = editForm?.property_enquiry_form_type;
          if (!selectedId || !selectedType) return null;
          return (
            nextOptions.find(
              (option) => option.id === selectedId && option.form_type === selectedType,
            ) || null
          );
        });
      } catch (e) {
        console.error("Tickets: failed to load property enquiry forms", e);
        if (!ignore) {
          setEditPropertyEnquiryOptions([]);
          setEditPropertyEnquiryForm(null);
        }
      } finally {
        if (!ignore) setEditPropertyEnquiryLoading(false);
      }
    };

    loadEditPropertyEnquiries();

    return () => {
      ignore = true;
    };
  }, [editingTicket, editForm?.type, editForm?.property_enquiry_form_id, editForm?.property_enquiry_form_type, editProperty?.id, mapPropertyEnquiryOptions]);

  const handleAddComment = async () => {
    if (!editingTicket || !commentText.trim()) return;
    setSubmitting(true);
    try {
      const res = await createTicketComment(editingTicket.id, {
        comment_text: commentText.trim(),
      });
      if (res?.comment) {
        setComments((c) => [...c, res.comment]);
        setCommentText("");
      }
    } catch (e) {
      console.error("Tickets: failed to add comment", e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPropertySearch = async (q) => {
    if (!q || q.length < 1) {
      setEditPropertyOptions([]);
      return;
    }
    setEditPropertyLoading(true);
    try {
      const results = await searchProperties(q, 20);
      setEditPropertyOptions(
        (results || []).map((p) => ({
          id: p.property_id,
          name: p.property_name,
          address_line1: p.address_line1,
          city: p.city,
        }))
      );
    } catch (e) {
      console.error("Tickets: property search failed", e);
    } finally {
      setEditPropertyLoading(false);
    }
  };

  // Counts for the filter chips. Use stats where available, fall back to list.
  const filterCounts = useMemo(() => {
    const urgentCount = tickets.filter((t) => t.priority === "Urgent").length;
    return {
      all: stats?.total || tickets.length,
      open: stats?.open || 0,
      in_progress: stats?.in_progress || 0,
      urgent: urgentCount,
    };
  }, [stats, tickets]);

  return (
    <Layout
      eyebrow="Track support requests and discussions"
      title="Tickets"
      rightSlot={
        <Button
          variant="contained"
          size="small"
          startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate("/tickets/new")}
          sx={{ px: 1.75, py: "8px", fontSize: 12.5 }}
        >
          New ticket
        </Button>
      }
    >
      {/* SEGMENTED FILTER BAR */}
      <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
        {FILTERS.map((f) => {
          const active = f.key === filterKey;
          return (
            <Box
              key={f.key}
              onClick={() => applyFilter(f.key)}
              sx={{
                px: 1.75,
                py: "7px",
                borderRadius: "6px",
                fontSize: 12.5,
                fontWeight: 600,
                border: `1px solid ${active ? theme.palette.brand.black : theme.palette.brand.border}`,
                backgroundColor: active ? theme.palette.brand.black : theme.palette.background.paper,
                color: active ? "#fff" : theme.palette.brand.inkMuted,
                cursor: "pointer",
                transition: "all .12s",
                "&:hover": {
                  borderColor: active ? theme.palette.brand.black : theme.palette.brand.borderStrong,
                  backgroundColor: active ? theme.palette.brand.blackSoft : theme.palette.brand.surfaceMuted,
                },
              }}
            >
              {f.label}
              <Box component="span" sx={{ ml: 0.75, opacity: 0.65 }}>
                {filterCounts[f.key] ?? 0}
              </Box>
            </Box>
          );
        })}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<TuneRoundedIcon sx={{ fontSize: 14 }} />}
          onClick={openFiltersPopover}
          aria-expanded={filterOpen ? "true" : undefined}
          aria-haspopup="true"
          sx={{
            fontSize: 12.5,
            py: "6px",
            px: 1.5,
            ...(hasAdvFilters
              ? {
                  borderColor: theme.palette.brand.black,
                  color: theme.palette.brand.ink,
                  fontWeight: 600,
                  backgroundColor: theme.palette.brand.surfaceMuted,
                }
              : {}),
          }}
        >
          Filters
        </Button>
      </Box>

      <Popover
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              p: 2,
              minWidth: 300,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.brand.border}`,
              boxShadow: theme.shadows[4],
            },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: theme.palette.brand.inkSoft,
            mb: 1.5,
          }}
        >
          Refine list
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Priority</InputLabel>
            <Select
              label="Priority"
              value={advDraft.priority}
              onChange={(e) => setAdvDraft((d) => ({ ...d, priority: e.target.value }))}
            >
              <MenuItem value="">Any</MenuItem>
              {PRIORITY_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={advDraft.type}
              onChange={(e) => setAdvDraft((d) => ({ ...d, type: e.target.value }))}
            >
              <MenuItem value="">Any</MenuItem>
              {TYPE_OPTIONS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            options={filterPropOptions}
            getOptionLabel={(o) => o?.name || ""}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            value={advDraft.property}
            loading={filterPropLoading}
            onInputChange={(_, q) => handleFilterPropertySearch(q)}
            onChange={(_, v) => setAdvDraft((d) => ({ ...d, property: v }))}
            renderInput={(params) => (
              <TextField {...params} label="Property" placeholder="Search…" />
            )}
          />
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", pt: 0.5 }}>
            <Button size="small" onClick={clearAdvFilters} disabled={!hasAdvFilters && !advDraft.priority && !advDraft.type && !advDraft.property}>
              Clear
            </Button>
            <Button size="small" variant="contained" onClick={applyAdvFilters}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* LIST PANEL */}
      <Panel
        eyebrow="Queue"
        title={loading ? "Loading…" : `${listTotal} tickets`}
      >
        {error && (
          <Box sx={{ p: 3, color: theme.palette.error.main, fontSize: 13 }}>{error}</Box>
        )}

        {!error && !loading && tickets.length === 0 && (
          <Box sx={{ p: 4, textAlign: "center", color: theme.palette.brand.inkSoft }}>
            No tickets match this filter.
          </Box>
        )}

        {!error && !loading && tickets.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: TICKET_LIST_GRID,
              gap: 1.75,
              px: 2.75,
              py: 1.25,
              borderBottom: `1px solid ${theme.palette.brand.border}`,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: theme.palette.brand.inkSoft,
              fontFamily: fonts.mono,
              alignItems: "center",
            }}
          >
            <Box>ID</Box>
            <Box>Title</Box>
            <Box>Property</Box>
            <Box>Type</Box>
            <Box>Priority</Box>
            <Box>Status</Box>
            <Box sx={{ textAlign: "right" }}>Age</Box>
          </Box>
        )}

        {!error &&
          tickets.map((t, i) => (
            <Box
              key={t.id}
              onClick={() => handleEditOpen(t)}
              sx={{
                display: "grid",
                gridTemplateColumns: TICKET_LIST_GRID,
                gap: 1.75,
                px: 2.75,
                py: 1.75,
                borderBottom:
                  i < tickets.length - 1 ? `1px solid ${theme.palette.brand.border}` : "none",
                alignItems: "center",
                cursor: "pointer",
                transition: "background .12s",
                "&:hover": { backgroundColor: theme.palette.brand.surfaceMuted },
              }}
            >
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: theme.palette.brand.inkSoft,
                  fontFamily: fonts.mono,
                }}
              >
                #{t.id}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: theme.palette.brand.ink,
                    lineHeight: 1.3,
                  }}
                >
                  {t.title}
                </Box>
                {t.latest_comment_text && (
                  <Box
                    sx={{
                      fontSize: 12,
                      color: theme.palette.brand.inkSoft,
                      mt: 0.25,
                      fontStyle: "italic",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    "{t.latest_comment_text}"
                  </Box>
                )}
              </Box>
              <Box
                onClick={(e) => {
                  if (t.property_id) {
                    e.stopPropagation();
                  }
                }}
                sx={{ fontSize: 12.5, color: theme.palette.brand.inkMuted, minWidth: 0 }}
              >
                {t.property_id ? (
                  <Box
                    component={RouterLink}
                    to={`/properties/${t.property_id}/details`}
                    sx={{
                      color: "inherit",
                      textDecoration: "none",
                      "&:hover": { color: theme.palette.brand.ink },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block",
                    }}
                  >
                    {t.property_name || "—"}
                  </Box>
                ) : (
                  "—"
                )}
              </Box>
              <Box sx={{ fontSize: 12, color: theme.palette.brand.inkSoft }}>
                {t.type || "—"}
              </Box>
              <Box>{t.priority && <Badge tone={t.priority}>{t.priority}</Badge>}</Box>
              <Box>{t.status && <Badge tone={t.status}>{t.status}</Badge>}</Box>
              <Box
                sx={{
                  fontSize: 11.5,
                  color: theme.palette.brand.inkSoft,
                  textAlign: "right",
                }}
              >
                {formatRelativeAge(t.created_at_utc)}
              </Box>
            </Box>
          ))}
      </Panel>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, v) => setPage(v)}
            size="small"
          />
        </Box>
      )}

      {/* EDIT DIALOG */}
      <Dialog open={Boolean(editingTicket)} onClose={handleEditClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: fonts.serif, fontWeight: 600, fontSize: 22 }}>
          Ticket #{editingTicket?.id}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          {editForm && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {editError && <Alert severity="error">{editError}</Alert>}
              <TextField
                label="Title"
                fullWidth
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={4}
                value={editForm.description}
                onChange={(e) =>
                  setEditForm({ ...editForm, description: e.target.value })
                }
              />
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    label="Priority"
                    value={editForm.priority}
                    onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                  >
                    {PRIORITY_OPTIONS.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={editForm.type}
                    onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                  >
                    {TYPE_OPTIONS.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Assigned to</InputLabel>
                  <Select
                    label="Assigned to"
                    value={editForm.assigned_to_id || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, assigned_to_id: e.target.value })
                    }
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.user_id} value={u.user_id}>
                        {u.name || u.email || `User ${u.user_id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Autocomplete
                options={editPropertyOptions}
                getOptionLabel={(o) => o?.name || ""}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                value={editProperty}
                loading={editPropertyLoading}
                onInputChange={(_, q) => handleEditPropertySearch(q)}
                onChange={(_, v) => setEditProperty(v)}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Property" placeholder="Search…" />
                )}
              />

              {editForm.type === "Management Pack" && (
                <Autocomplete
                  options={editPropertyEnquiryOptions}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) => (
                    option?.id === value?.id && option?.form_type === value?.form_type
                  )}
                  value={editPropertyEnquiryForm}
                  loading={editPropertyEnquiryLoading}
                  disabled={!editProperty}
                  onChange={(_, value) => setEditPropertyEnquiryForm(value)}
                  noOptionsText={editProperty ? "No property enquiries found." : "Select a property first."}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Property Enquiry Form"
                      placeholder="Select an LPE1 or FME1 questionnaire"
                    />
                  )}
                />
              )}

              <TextField
                label="Requester name"
                size="small"
                fullWidth
                value={editForm.requester_name}
                onChange={(e) =>
                  setEditForm({ ...editForm, requester_name: e.target.value })
                }
              />

              <Divider sx={{ my: 1 }} />

              <Typography
                sx={{ fontFamily: fonts.serif, fontSize: 16, fontWeight: 600 }}
              >
                Comments
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {comments.length === 0 ? (
                  <Typography variant="body2" sx={{ color: theme.palette.brand.inkSoft }}>
                    No comments yet.
                  </Typography>
                ) : (
                  comments.map((c) => (
                    <Box
                      key={c.id}
                      sx={{
                        backgroundColor: theme.palette.brand.surfaceMuted,
                        border: `1px solid ${theme.palette.brand.border}`,
                        borderRadius: "6px",
                        p: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: 11,
                          color: theme.palette.brand.inkSoft,
                          mb: 0.5,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          fontWeight: 600,
                        }}
                      >
                        {c.created_by_name || c.created_by_email || "Unknown"} ·{" "}
                        {formatRelativeAge(c.created_at_utc)}
                      </Box>
                      <Typography variant="body2">{c.comment_text}</Typography>
                    </Box>
                  ))
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add a comment…"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddComment}
                  disabled={submitting || !commentText.trim()}
                >
                  Post
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleEditClose}>Close</Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={submitting}>
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
